import { useState, useEffect } from 'react';

interface Player {
    id: string;
    firstName: string;
    lastName: string;
    grade: number;
    gamerTag?: string;
    discordId?: string;
    discordUsername?: string;
    parentName?: string;
    parentDiscordUsername?: string;
    games: {
        smashSolo?: boolean;
        smashCrews?: boolean;
        smashJVCrews?: boolean;
        madden?: boolean;
        rocketLeague?: boolean;
        marioKart?: boolean;
        fortnite?: boolean;
        broadcasting?: boolean;
    };
    permissionSlipOnFile: boolean;
    status: string;
    season?: string;
    startggHandle?: string;
}

interface PlayerEditModalProps {
    player: Player | null; // null = creating new player
    isOpen: boolean;
    onClose: () => void;
    onSave: (player: Partial<Player> & { id?: string }) => Promise<void>;
    discordInviteUrl?: string;
}

const GAMES = [
    { key: 'smashSolo', label: 'Smash Solos', emoji: '🎮' },
    { key: 'smashCrews', label: 'Smash Crews', emoji: '👥' },
    { key: 'smashJVCrews', label: 'Smash JV Crews', emoji: '🛡️' },
    { key: 'madden', label: 'Madden', emoji: '🏈' },
    { key: 'rocketLeague', label: 'Rocket League', emoji: '🚗' },
    { key: 'marioKart', label: 'Mario Kart', emoji: '🏎️' },
    { key: 'fortnite', label: 'Fortnite', emoji: '🔫' },
    { key: 'broadcasting', label: 'Broadcasting', emoji: '📺' },
] as const;

const STATUS_OPTIONS = ['active', 'inactive', 'ineligible', 'withdrawn'];

export default function PlayerEditModal({
    player,
    isOpen,
    onClose,
    onSave,
    discordInviteUrl = 'https://discord.gg/peckpirates'
}: PlayerEditModalProps) {
    const isNew = !player;

    // Form state
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [grade, setGrade] = useState(9);
    const [gamerTag, setGamerTag] = useState('');
    const [startggHandle, setStartggHandle] = useState('');
    const [discordUsername, setDiscordUsername] = useState('');
    const [parentName, setParentName] = useState('');
    const [parentDiscordUsername, setParentDiscordUsername] = useState('');
    const [games, setGames] = useState<Player['games']>({});
    const [status, setStatus] = useState('inactive');
    const [permissionSlipOnFile, setPermissionSlipOnFile] = useState(false);

    const [saving, setSaving] = useState(false);
    const [copied, setCopied] = useState(false);

    // Reset form when player changes
    useEffect(() => {
        if (player) {
            setFirstName(player.firstName || '');
            setLastName(player.lastName || '');
            setGrade(player.grade || 9);
            setGamerTag(player.gamerTag || '');
            setStartggHandle(player.startggHandle || '');
            setDiscordUsername(player.discordUsername || '');
            setParentName(player.parentName || '');
            setParentDiscordUsername(player.parentDiscordUsername || '');
            setGames(player.games || {});
            setStatus(player.status || 'inactive');
            setPermissionSlipOnFile(player.permissionSlipOnFile || false);
        } else {
            // Reset for new player
            setFirstName('');
            setLastName('');
            setGrade(9);
            setGrade(9);
            setGamerTag('');
            setStartggHandle('');
            setDiscordUsername('');
            setParentName('');
            setParentDiscordUsername('');
            setGames({});
            setStatus('inactive');
            setPermissionSlipOnFile(false);
        }
    }, [player, isOpen]);

    const toggleGame = (key: keyof Player['games']) => {
        setGames(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const copyInvite = async () => {
        await navigator.clipboard.writeText(discordInviteUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleSave = async () => {
        if (!firstName.trim() || !lastName.trim()) {
            alert('First and last name are required');
            return;
        }

        setSaving(true);
        try {
            await onSave({
                ...(player ? { id: player.id } : {}),
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                grade,
                gamerTag: gamerTag.trim() || undefined,
                startggHandle: startggHandle.trim() || undefined,
                discordUsername: discordUsername.trim() || undefined,
                parentName: parentName.trim() || undefined,
                parentDiscordUsername: parentDiscordUsername.trim() || undefined,
                games,
                status,
                permissionSlipOnFile,
            });
            onClose();
        } catch (err) {
            console.error('Error saving player:', err);
            alert('Failed to save player');
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative bg-secondary-800 rounded-xl border border-secondary-700 shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-secondary-800 border-b border-secondary-700 px-6 py-4 flex items-center justify-between">
                    <h2 className="text-xl font-display font-bold text-white">
                        {isNew ? '➕ Add New Player' : `✏️ Edit: ${player.firstName} ${player.lastName}`}
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-secondary-400 hover:text-white text-xl"
                    >
                        ✕
                    </button>
                </div>

                {/* Form */}
                <div className="p-6 space-y-6">
                    {/* Basic Info */}
                    <section>
                        <h3 className="text-sm font-semibold text-secondary-400 mb-3 uppercase tracking-wide">
                            Basic Info
                        </h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="label">First Name *</label>
                                <input
                                    type="text"
                                    value={firstName}
                                    onChange={e => setFirstName(e.target.value)}
                                    className="input"
                                    placeholder="John"
                                />
                            </div>
                            <div>
                                <label className="label">Last Name *</label>
                                <input
                                    type="text"
                                    value={lastName}
                                    onChange={e => setLastName(e.target.value)}
                                    className="input"
                                    placeholder="Doe"
                                />
                            </div>
                            <div>
                                <label className="label">Grade</label>
                                <select
                                    value={grade}
                                    onChange={e => setGrade(Number(e.target.value))}
                                    className="input"
                                >
                                    {[6, 7, 8, 9, 10, 11, 12].map(g => (
                                        <option key={g} value={g}>{g}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="label">Gamer Tag</label>
                                <input
                                    type="text"
                                    value={gamerTag}
                                    onChange={e => setGamerTag(e.target.value)}
                                    className="input"
                                    placeholder="PandaMan22"
                                />
                            </div>
                        </div>
                    </section>

                    {/* Discord */}
                    <section>
                        <h3 className="text-sm font-semibold text-secondary-400 mb-3 uppercase tracking-wide">
                            Discord
                        </h3>
                        <div className="flex gap-2">
                            <div className="flex-1">
                                <label className="label">Discord Username</label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-500">@</span>
                                    <input
                                        type="text"
                                        value={discordUsername}
                                        onChange={e => setDiscordUsername(e.target.value)}
                                        className="input pl-7"
                                        placeholder="username"
                                    />
                                </div>
                            </div>
                            <div className="flex items-end">
                                <button
                                    onClick={copyInvite}
                                    className={`btn-secondary text-sm whitespace-nowrap ${copied ? 'bg-green-500/20 text-green-400' : ''}`}
                                >
                                    {copied ? '✓ Copied!' : '🔗 Copy Invite'}
                                </button>
                            </div>
                        </div>
                    </section>

                    {/* Start.gg */}
                    <section>
                        <h3 className="text-sm font-semibold text-secondary-400 mb-3 uppercase tracking-wide">
                            Integration
                        </h3>
                        <div>
                            <label className="label">Start.gg Handle</label>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-500">gg/</span>
                                <input
                                    type="text"
                                    value={startggHandle}
                                    onChange={e => setStartggHandle(e.target.value)}
                                    className="input pl-10"
                                    placeholder="handle"
                                />
                                {/* <span className="text-xs text-secondary-500 mt-1 block">To edit, please Implement State first (Self-Correction)</span> */}
                            </div>
                        </div>
                    </section>

                    {/* Games */}
                    <section>
                        <h3 className="text-sm font-semibold text-secondary-400 mb-3 uppercase tracking-wide">
                            Games
                        </h3>
                        <div className="grid grid-cols-2 gap-2">
                            {GAMES.map(game => (
                                <label
                                    key={game.key}
                                    className={`flex items-center gap-2 p-3 rounded-lg cursor-pointer transition-colors ${games[game.key as keyof Player['games']]
                                        ? 'bg-primary-500/20 border border-primary-500/50'
                                        : 'bg-secondary-700 border border-transparent hover:bg-secondary-600'
                                        }`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={!!games[game.key as keyof Player['games']]}
                                        onChange={() => toggleGame(game.key as keyof Player['games'])}
                                        className="rounded border-secondary-500"
                                    />
                                    <span className="text-lg">{game.emoji}</span>
                                    <span className="text-sm text-white">{game.label}</span>
                                </label>
                            ))}
                        </div>
                    </section>

                    {/* Parent/Guardian */}
                    <section>
                        <h3 className="text-sm font-semibold text-secondary-400 mb-3 uppercase tracking-wide">
                            Parent/Guardian
                        </h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="label">Parent Name</label>
                                <input
                                    type="text"
                                    value={parentName}
                                    onChange={e => setParentName(e.target.value)}
                                    className="input"
                                    placeholder="Jane Doe"
                                />
                            </div>
                            <div>
                                <label className="label">Parent Discord</label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-500">@</span>
                                    <input
                                        type="text"
                                        value={parentDiscordUsername}
                                        onChange={e => setParentDiscordUsername(e.target.value)}
                                        className="input pl-7"
                                        placeholder="parent_username"
                                    />
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Status */}
                    <section>
                        <h3 className="text-sm font-semibold text-secondary-400 mb-3 uppercase tracking-wide">
                            Status
                        </h3>
                        <div className="flex flex-wrap items-center gap-4">
                            <div>
                                <label className="label">Status</label>
                                <select
                                    value={status}
                                    onChange={e => setStatus(e.target.value)}
                                    className="input w-auto"
                                >
                                    {STATUS_OPTIONS.map(s => (
                                        <option key={s} value={s}>
                                            {s.charAt(0).toUpperCase() + s.slice(1)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <label className="flex items-center gap-2 cursor-pointer mt-6">
                                <input
                                    type="checkbox"
                                    checked={permissionSlipOnFile}
                                    onChange={e => setPermissionSlipOnFile(e.target.checked)}
                                    className="rounded border-secondary-500 w-5 h-5"
                                />
                                <span className="text-white">Permission Slip On File</span>
                            </label>
                        </div>
                    </section>
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 bg-secondary-800 border-t border-secondary-700 px-6 py-4 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="btn-secondary"
                        disabled={saving}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        className="btn-primary"
                        disabled={saving}
                    >
                        {saving ? 'Saving...' : isNew ? 'Create Player' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </div>
    );
}
